import React from 'react'
import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import PublishUtils from '../../../helpers/PublishUtils'
import { PRELOAD_LABELS_TTL } from './shacl-form-preload-labels'

export const configOptions = [
  {
    name: 'shapeQuery',
    type: 'yasgui',
    label: (
      <span>
        SPARQL CONSTRUCT query to define SHACL shapes for the form<br />
        <strong>The results are combined with the results from Turtle</strong>
      </span>
    ),
    defaultQuery: ``,
  },
  {
    name: 'shapeTtl',
    type: 'turtle',
    label: (
      <span>
        Turtle to define SHACL shapes for the form<br />
        <strong>The results are combined with results from the CONSTRUCT query</strong>
      </span>
    ),
  },
  {
    name: 'graph',
    type: 'text',
    label: (
      <span>
        Graph to store the form submission data.<br />
        <strong>It should be a graph URI <strong>without</strong> angle bracket.</strong>
      </span>
    ),
    topicValueInterpolationEnabled: true
  },
  {
    name: 'uriPrefix',
    type: 'text',
    label: 'URI prefix for creating new (sub)instances or normalizing IRIs without scheme (e.g., "https://wistor.nl/resource/")',
  },
  {
    name: 'dataQuery',
    type: 'yasgui',
    label: (
      <span>
      SPARQL CONSTRUCT query to load data into the form for editing<br />
      <strong>Leave empty to create a new resource</strong>
    </span>
    ),
    defaultQuery: ``,
  },
  {
    name: 'n3Rules',
    type: 'turtle',
    label: (
      <span>
        <strong>N3 Rules</strong>
        <br />
        N3 rules to execute before form submission. Rules are applied to the data using N3.js reasoning.
      </span>
    ),
  },
  {
    name: 'allowSubmitWithValidationErrors',
    type: 'boolean',
    label: 'Allow form submission even if there are validation errors',
  },
  {
    name: 'successMessage',
    type: 'text',
    label: 'Success message to display after form submission',
  },
  {
    name: 'failureMessage',
    type: 'text',
    label: 'Failure message to display on submission error',
  },
  {
    name: 'showLoadingIndicator',
    type: 'boolean',
    label: 'Show loading indicator while form is initializing or saving',
  },
  {
    name: 'loadingMessage',
    type: 'text',
    label: 'Custom loading message',
  },
  {
    name: 'savingMessage',
    type: 'text',
    label: 'Custom saving message',
  },
  {
    name: 'closePopup',
    type: 'boolean',
    label: 'Close popup/dialog after successful submission',
  },
  {
    name: 'ruleToTriggerAfterSubmission',
    type: 'text',
    label: 'Wistor rule to trigger after successful submission (tag or URI)',
  },
  {
    name: 'ruleParameters',
    type: 'json-text',
    label: 'Parameters for the rule (JSON format)',
  },
  {
    name: 'submitButtonTitle',
    type: 'text',
    label: 'Submit button text',
  },
  {
    name: 'submitButtonBeginIcon',
    type: 'text',
    label: 'Submit button begin icon - available prefixes: `fa-` `mui-` `radix-`',
  },
  {
    name: 'submitButtonEndIcon',
    type: 'text',
    label: 'Submit button end icon - available prefixes: `fa-` `mui-` `radix-`',
  },
  {
    name: 'submitButtonCustomStyling',
    type: 'boolean',
    label: 'Enable custom styling for submit button',
  },
  {
    name: 'submitButtonBorderRadius',
    type: 'text',
    label: 'Custom Styling: button-border-radius (examples: "2lh", "50%", "1.2em")',
  },
  {
    name: 'submitButtonTextColor',
    type: 'text',
    label: 'Custom Styling: text-color (examples: "white", "#FFFFFF")',
  },
  {
    name: 'submitButtonBgColor',
    type: 'text',
    label: 'Custom Styling: background-color (examples: "blue", "#007bff")',
  },
  {
    name: 'submitButtonHasShadow',
    type: 'boolean',
    label: 'Custom Styling: enable button shadow',
  },
  {
    name: 'submitButtonFontSize',
    type: 'text',
    label: 'Custom Styling: button-font-size',
  },
  {
    name: 'submitButtonFontFamily',
    type: 'text',
    label: 'Custom Styling: button-font-family',
  },
  {
    name: 'preloadLabelsTtl',
    type: 'boolean',
    label: 'Preload common RDF/OWL/SHACL labels into the shape (rdfs:label definitions for standard vocabularies)',
    helpComponent: PreloadLabelsTtlHelp,
  },
  {
    name: 'language',
    type: 'text',
    label: (
      <span>
        <strong>data-language:</strong> Language code for langStrings (e.g., "en", "de")<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'viewMode',
    type: 'boolean',
    label: (
      <span>
        <strong>data-view:</strong> Enable read-only viewer mode (Only works for default theme for now)<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'collapseMode',
    type: 'text',
    label: (
      <span>
        <strong>data-collapse:</strong> Collapse groups/nested properties ("" = collapsed, "open" = expanded)<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'dense',
    type: 'boolean',
    label: (
      <span>
        <strong>data-dense:</strong> Compact rendering mode (default: true)<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'hierarchyColors',
    type: 'text',
    label: (
      <span>
        <strong>data-hierarchy-colors:</strong> Comma-separated CSS colors for nesting levels<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'showNodeIds',
    type: 'boolean',
    label: (
      <span>
        <strong>data-show-node-ids:</strong> Display SHACL node shape subject IDs<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'showRootShapeLabel',
    type: 'boolean',
    label: (
      <span>
        <strong>data-show-root-shape-label:</strong> Display root shape heading<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'loadingText',
    type: 'text',
    label: (
      <span>
        <strong>data-loading:</strong> Text shown during form initialization<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'ignoreOwlImports',
    type: 'boolean',
    label: (
      <span>
        <strong>data-ignore-owl-imports:</strong> Disable automatic owl:imports fetching<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'valuesNamespace',
    type: 'text',
    label: (
      <span>
        <strong>data-values-namespace:</strong> RDF namespace for new subjects<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'valuesGraph',
    type: 'text',
    label: (
      <span>
        <strong>data-values-graph:</strong> Named graph IRI for created data<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'generateNodeShapeReference',
    type: 'text',
    label: (
      <span>
        <strong>data-generate-node-shape-reference:</strong> Predicate for shape conformance triple<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'strictUpdate',
    type: 'boolean',
    label: (
      <span className="inline-block">use strict updates. note:
        <ul>
          <li>- this is <em>incompatible when blank nodes are involved!</em></li>
          <li>- It's not clear yet whether this has <strong>undesired side-effects</strong>!</li>
        </ul>
      </span>
    ),
  },
  {
    name: 'proxyUrl',
    type: 'text',
    label: (
      <span>
        <strong>data-proxy:</strong> Proxy URL for CORS-restricted resources<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  PublishUtils.getTimestampParametersDef()
] as const

export type ShaclFormConfigOptions = ConfigurablePropsFromDescription<typeof configOptions>

function PreloadLabelsTtlHelp() {
  return (
    <div>
      <p className="mb-3">
        When enabled, a set of <code>rdfs:label</code> definitions for common RDF vocabulary terms
        is preloaded into the SHACL shapes before your own shapes are applied.
      </p>
      <p className="mb-3">
        This improves the form's display by providing human-readable labels for standard terms
        that might otherwise show as raw URIs.
      </p>
      <p className="mb-2 font-semibold">Covered vocabularies:</p>
      <ul className="list-disc pl-4 mb-3">
        <li><strong>RDF / RDFS</strong> — types, properties, classes</li>
        <li><strong>XSD</strong> — all datatypes (string, integer, date, dateTime, dayTimeDuration, gYear, etc.)</li>
        <li><strong>OWL</strong> — classes, property types, restrictions, cardinalities</li>
        <li><strong>SHACL</strong> — shapes, constraints, targets, node kinds, severities, logical operators</li>
        <li><strong>SKOS</strong> — concepts, labels, semantic relations</li>
        <li><strong>Dublin Core Terms</strong> — title, creator, created, modified, etc.</li>
      </ul>
      <p className="text-sm mb-2">
        Your own shapes (from TTL or CONSTRUCT query) are loaded <em>after</em> the preloaded labels,
        so any labels you define will override the defaults.
      </p>
      <details>
        <summary className="cursor-pointer font-semibold text-sm mb-1">View preloaded Turtle</summary>
        <pre style={{ fontSize: '11px', maxHeight: '400px', overflow: 'auto', background: '#f8fafc', padding: '8px', borderRadius: '4px' }}>
          {PRELOAD_LABELS_TTL.trim()}
        </pre>
      </details>
    </div>
  )
}
